import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { AlertTriangle, Bell, RotateCcw } from "lucide-react";

const SEV_COLOR = {
  Critical: "text-[color:var(--critical)] border-[color:var(--critical)]/50",
  High: "text-[color:var(--warning)] border-[color:var(--warning)]/50",
  Medium: "text-[color:var(--info)] border-[color:var(--info)]/50",
  Low: "text-[color:var(--success)] border-[color:var(--success)]/50",
};

export default function Sustain() {
  const [trend, setTrend] = useState([]);
  const [posture, setPosture] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    const [t, p, a] = await Promise.all([
      api.get("/api/sustain/cost-trend"),
      api.get("/api/sustain/security-posture"),
      api.get("/api/sustain/alerts"),
    ]);
    setTrend(t.data);
    setPosture(p.data);
    setAlerts(a.data);
  };

  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Clear all Sustain alerts?")) return;
    try {
      await api.delete("/api/sustain/reset");
      await load();
    } catch {}
  };

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ stage · 05</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            Keep it from decaying back into legacy.
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-2xl">
            Post-migration governance: cost, drift, and security posture — with
            alerts routed to the workspace.
          </p>
        </div>
        <button
          data-testid="reset-sustain-btn"
          onClick={reset}
          className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
        >
          <RotateCcw size={14} /> Reset
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Cost trend */}
        <div className="xl:col-span-2 border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="eyebrow">/ cost trend · legacy vs cloud</div>
              <div className="mt-1 mono text-xs text-[color:var(--text-3)]">
                12-month rolling
              </div>
            </div>
          </div>
          <div className="h-72" data-testid="cost-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="grad-legacy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3b30" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#ff3b30" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad-cloud" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00f0ff" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00f0ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" stroke="#52525b" />
                <YAxis
                  stroke="#52525b"
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0e0e0e",
                    border: "1px solid #27272a",
                    color: "#fff",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12,
                    borderRadius: 0,
                  }}
                  formatter={(v) => `$${Number(v).toLocaleString()}`}
                />
                <Area
                  type="monotone"
                  dataKey="legacy"
                  stroke="#ff3b30"
                  strokeWidth={1.5}
                  fill="url(#grad-legacy)"
                />
                <Area
                  type="monotone"
                  dataKey="cloud"
                  stroke="#00f0ff"
                  strokeWidth={1.5}
                  fill="url(#grad-cloud)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-3 mono text-[10px] uppercase tracking-widest">
            <span className="flex items-center gap-2 text-[color:var(--text-2)]">
              <span className="w-3 h-0.5 bg-[color:var(--critical)]" /> legacy runtime
            </span>
            <span className="flex items-center gap-2 text-[color:var(--text-2)]">
              <span className="w-3 h-0.5 bg-[color:var(--accent)]" /> cloud modern
            </span>
          </div>
        </div>

        {/* Security posture */}
        <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5">
          <div className="eyebrow">/ security posture</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {["Critical", "High", "Medium", "Low"].map((k) => (
              <div
                key={k}
                data-testid={`posture-${k.toLowerCase()}`}
                className={`border p-3 ${SEV_COLOR[k]}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)]">
                  {k}
                </div>
                <div className="mono text-2xl text-white mt-1">
                  {posture?.by_severity?.[k] ?? 0}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-[color:var(--border)]">
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              average risk score
            </div>
            <div className="mono text-3xl mt-1">
              {posture?.avg_risk ?? "—"}
              <span className="text-[color:var(--text-3)] text-lg"> / 100</span>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)]">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center gap-2">
          <Bell size={14} className="text-[color:var(--accent)]" />
          <div className="eyebrow">/ drift & governance alerts</div>
          <span className="ml-auto mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            live feed
          </span>
        </div>
        <div className="divide-y divide-[color:var(--border)]">
          {alerts.map((a) => (
            <div
              key={a.id}
              data-testid={`alert-${a.id}`}
              className="p-4 flex items-start gap-3 hover:bg-[color:var(--bg-2)] transition-colors duration-150"
            >
              <AlertTriangle
                size={14}
                className={SEV_COLOR[a.severity].split(" ")[0]}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{a.title}</div>
                <div className="mono text-xs text-[color:var(--text-2)] mt-1">
                  {a.body}
                </div>
              </div>
              <span
                className={`mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${
                  SEV_COLOR[a.severity]
                }`}
              >
                {a.severity}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
